import { getFirebaseConfig } from '../config/firebase.config';

export type MomentSearchSessionGroup = 'live' | 'upcoming' | 'past';
export type MomentSearchResultGroup = 'liveMoments' | MomentSearchSessionGroup | 'moments' | 'publishers';

export interface MomentSearchSessionResult {
  type: 'session';
  id: string;
  group: MomentSearchSessionGroup;
  title: string;
  subtitle: string;
  publisherName: string;
  publisherHandle: string;
  category: string;
  categoryLabel: string;
  categoryIcon: string;
  momentTypeLabels: string[];
  status: string;
  startTimeIso: string | null;
  thumbnailUrl: string | null;
  fanUrl: string;
}

export interface MomentSearchMomentResult {
  type: 'moment';
  id: string;
  sessionId: string;
  title: string;
  description: string;
  momentType: string;
  momentTypeLabel: string;
  momentTypeIcon: string;
  sessionTitle: string;
  publisherName: string;
  category: string;
  categoryLabel: string;
  categoryIcon: string;
  occurredAtIso: string | null;
  thumbnailUrl: string | null;
  fanUrl: string;
}

export interface MomentTickerResult extends Omit<MomentSearchMomentResult, 'type'> {
  type: 'liveMoment';
}

export interface MomentSearchPublisherResult {
  type: 'publisher';
  id: string;
  displayName: string;
  handle: string;
  category: string;
  categoryLabel: string;
  categoryIcon: string;
  followerCount: number;
  publishedMomentCount: number;
  avatarUrl: string | null;
  fanUrl: string;
}

export interface MomentSearchResponse {
  query: string;
  normalizedQuery: string;
  generatedAtIso: string;
  groups: {
    liveMoments: MomentTickerResult[];
    live: MomentSearchSessionResult[];
    upcoming: MomentSearchSessionResult[];
    moments: MomentSearchMomentResult[];
    past: MomentSearchSessionResult[];
    publishers: MomentSearchPublisherResult[];
  };
  meta: {
    defaultResults: boolean;
    minimumQueryLength: number;
    searched: boolean;
    pagination?: Record<MomentSearchResultGroup, {
      hasMore: boolean;
      nextCursor: string | null;
    }>;
  };
}

interface MomentSearchRequest {
  query?: string;
  limit?: number;
  group?: MomentSearchResultGroup;
  cursor?: string;
}

interface MomentTickerRequest {
  query?: string;
  limit?: number;
}

export interface MomentTickerResponse {
  query: string;
  normalizedQuery: string;
  generatedAtIso: string;
  items: MomentTickerResult[];
  meta: {
    minimumQueryLength: number;
    pollIntervalMs: number;
    searched: boolean;
  };
}

const buildPublicMomentUrl = (
  functionName: 'momentSearch' | 'momentTicker',
  params: URLSearchParams,
): string => {
  const config = getFirebaseConfig();
  const baseUrl = import.meta.env.VITE_ENVIRONMENT === 'local' && import.meta.env.VITE_USE_EMULATORS === 'true'
    ? `http://localhost:5001/${config.projectId}/europe-west1/${functionName}`
    : `https://europe-west1-${config.projectId}.cloudfunctions.net/${functionName}`;

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

const buildMomentSearchUrl = (request: MomentSearchRequest): string => {
  const params = new URLSearchParams();
  const query = request.query?.slice(0, 80) ?? '';
  if (query) {
    params.set('q', query);
  }
  if (typeof request.limit === 'number') {
    params.set('limit', String(request.limit));
  }
  if (request.group) {
    params.set('group', request.group);
  }
  if (request.cursor) {
    params.set('cursor', request.cursor);
  }

  return buildPublicMomentUrl('momentSearch', params);
};

const buildMomentTickerUrl = (request: MomentTickerRequest): string => {
  const params = new URLSearchParams();
  const query = request.query?.slice(0, 80) ?? '';
  if (query) {
    params.set('q', query);
  }
  if (typeof request.limit === 'number') {
    params.set('limit', String(request.limit));
  }

  return buildPublicMomentUrl('momentTicker', params);
};

export async function searchPublicMoments(request: MomentSearchRequest): Promise<MomentSearchResponse> {
  const response = await fetch(buildMomentSearchUrl(request), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Moment Search request failed with status ${response.status}`);
  }

  return await response.json() as MomentSearchResponse;
}

export async function fetchLiveMomentTicker(request: MomentTickerRequest): Promise<MomentTickerResponse> {
  const response = await fetch(buildMomentTickerUrl(request), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Moment Ticker request failed with status ${response.status}`);
  }

  return await response.json() as MomentTickerResponse;
}
